import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player/file';

import { useProgress } from '../../../hooks/progress';
import { convertHoursMinutesSecondsToSeconds } from '../../../utils/functions';

import VideoModalQuiz from './VideoModalQuiz';

import { Container, VimeoWrapper, ContainerOverlay, EventChat } from './styles';
import { CourseVideoPlayerProps, getResumeSeconds } from './videoProvider';

const CourseBunnyComponent: React.FC<CourseVideoPlayerProps> = ({
  actualTime,
  isLoading,
  isPlaying,
  large = false,
  quizzes = [],
  url,
  video,
  onFinish,
  onPause,
  setActualTime,
  setIsPlaying,
}) => {
  const [isOverlayed, setIsOverlayed] = useState(false);
  const [quizIndex, setQuizIndex] = useState(0);

  const { videos } = useProgress();

  const timeToStart = useMemo(() => getResumeSeconds(video, videos), [video, videos]);

  const playerRef = useRef<ReactPlayer>(null);
  const hasResumedRef = useRef(false);

  /**
   * onPause/onEnded do react-player genérico não recebem payload, mas as telas que
   * consomem este player gravam progresso a partir de { seconds, percent, duration }
   * (formato dos eventos do SDK do Vimeo). Reconstruímos o mesmo formato para manter
   * o contrato com as páginas inalterado.
   */
  const buildPlaybackInfo = useCallback((forceComplete = false) => {
    const seconds = playerRef.current?.getCurrentTime() ?? 0;
    const duration = playerRef.current?.getDuration() ?? 0;
    const percent = forceComplete ? 1 : duration > 0 ? seconds / duration : 0;

    return { seconds, duration, percent };
  }, []);

  const handlePauseVideo = useCallback(() => {
    setIsPlaying && setIsPlaying(false);

    const info = buildPlaybackInfo();

    if (info.seconds > timeToStart) {
      onPause && onPause(info);
    }
  }, [onPause, timeToStart, setIsPlaying, buildPlaybackInfo]);

  const handleEndVideo = useCallback(() => {
    setIsPlaying && setIsPlaying(false);
    onFinish && onFinish(buildPlaybackInfo(true));
  }, [onFinish, setIsPlaying, buildPlaybackInfo]);

  const handleProgressVideo = useCallback(
    (info: any) => {
      if (isPlaying) {
        setActualTime && setActualTime(info);
      }
    },
    [setActualTime, isPlaying],
  );

  // O componente não é remontado ao trocar de aula, então a retomada precisa ser
  // rearmada sempre que a url muda.
  useEffect(() => {
    hasResumedRef.current = false;
  }, [url]);

  // Em HLS o seek só é aceito depois do onReady, diferente do player do Vimeo.
  const handlePlayerReady = useCallback(() => {
    if (!hasResumedRef.current && timeToStart > 0) {
      playerRef.current?.seekTo(timeToStart, 'seconds');
      hasResumedRef.current = true;
    }
  }, [timeToStart]);

  const toggleQuizModal = (from: string): void => {
    switch (from) {
      case 'timer':
        setIsPlaying && setIsPlaying(false);
        setIsOverlayed(true);
        break;
      case 'modal':
        setIsPlaying && setIsPlaying(true);
        setIsOverlayed(false);
        break;
      default:
        break;
    }
  };

  const quizPositions = useMemo(
    () =>
      quizzes.map((item, index) => ({
        id: index,
        time: convertHoursMinutesSecondsToSeconds(item.videoposition),
      })),
    [quizzes],
  );

  useEffect(() => {
    if (actualTime && quizzes.length > 0) {
      const closest = quizPositions.reduce((a, b) => {
        const needle = actualTime.playedSeconds;
        return Math.abs(b.time - needle) < Math.abs(a.time - needle) ? b : a;
      });
      setQuizIndex(closest.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualTime]);

  return (
    <Container large={large} from="course">
      {quizzes.length > 0 && (
        <ContainerOverlay isVisible={isOverlayed}>
          <VideoModalQuiz
            actualTime={actualTime}
            quiz={quizzes[quizIndex]}
            onAction={toggleQuizModal}
          />
        </ContainerOverlay>
      )}
      <VimeoWrapper
        className={`${isOverlayed ? 'blured' : ''}`}
        hasChat={url?.includes('event')}
      >
        {url && !isLoading && (
          <ReactPlayer
            ref={playerRef}
            url={url}
            playing={isPlaying}
            progressInterval={950}
            width="100%"
            height="100%"
            controls
            config={{
              attributes: {
                controlsList: 'nodownload',
                disablePictureInPicture: true,
              },
            }}
            onReady={handlePlayerReady}
            onPause={handlePauseVideo}
            onEnded={handleEndVideo}
            onProgress={(e: any) => handleProgressVideo(e)}
            onPlay={() => setIsPlaying && setIsPlaying(true)}
          />
        )}
      </VimeoWrapper>
      {url?.includes('event') && (
        <EventChat>
          <iframe title="chat event container" src={video?.chaturl} frameBorder="0" />
        </EventChat>
      )}
    </Container>
  );
};

export default CourseBunnyComponent;
